import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { getBrowserTimeZone } from '../../lib/time-zone.js';
import { StatementImportHistory } from './statement-import-history.js';
import { TransferChoice, TransferDialog } from './transfers.js';
import { api } from '../../lib/api.js';
import {
  Button,
  CurrencyInput,
  DatePicker,
  DateRangePicker,
  IconButton,
  Input,
  Modal,
  Select,
  SegmentedControl,
  StatusPill,
  formatCivilDate,
  formatCivilRange,
  formatCurrency,
  type CivilDate,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Download,
  FileUp,
  Landmark,
  PencilLine,
  Plus,
  RotateCcw,
  Ban,
  Archive,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  financeAccountsApi,
  financeMovementsApi,
  getApiError,
  type Currency,
  type FinanceAccount,
  type FinanceAccountActivity,
  type FinanceAccountKind,
  type FinanceMovement,
  type FinanceMovementDirection,
  type FinanceMovementKind,
  type FinanceMovementSort,
  type FinanceMovementStatus,
  type FinancialObligationDirection,
  type StatementImportResult,
} from '../../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  MultiFilter,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
  SummaryStrip,
} from '../../app/data-view.js';
import { choiceChips } from '../../app/choice-filters.js';
import { financeViewDefaults, type FinanceView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { ReconciliationPanel } from './company-reconciliation.js';
import { CompanyClosing } from './company-closing.js';
import { ReconciliationRulesPanel } from './company-reconciliation-rules.js';
import { PageHeader } from '../../app/page-title.js';

export interface FinanceCapabilities {
  canSettle: boolean;
  canManageRules: boolean;
  canCreateMovement: boolean;
  canUpdateMovement: boolean;
  canCancelMovement: boolean;
  canCreateAccount: boolean;
  canUpdateAccount: boolean;
  canArchiveAccount: boolean;
  canClose: boolean;
  canReopen: boolean;
}

const kindLabels: Record<FinanceAccountKind, string> = {
  BANK: 'Banco',
  CASH: 'Caixa',
  ACQUIRER: 'Adquirente',
};
const accountKinds = Object.entries(kindLabels).map(([value, label]) => ({
  value,
  label,
})) as Array<{ value: FinanceAccountKind; label: string }>;
const activityOptions: Array<{ value: FinanceAccountActivity; label: string }> = [
  { value: 'ACTIVE', label: 'Ativas' },
  { value: 'INACTIVE', label: 'Arquivadas' },
];
const flowOptions: Array<{ value: FinanceMovementDirection; label: string }> = [
  { value: 'INFLOW', label: 'Entradas' },
  { value: 'OUTFLOW', label: 'Saídas' },
];
const movementStatusOptions: Array<{ value: FinanceMovementStatus; label: string }> = [
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'CANCELED', label: 'Cancelados' },
];
const currencies: Array<{ value: Currency; label: string }> = [
  { value: 'BRL', label: 'Real' },
  { value: 'USD', label: 'Dólar' },
  { value: 'ARS', label: 'Peso argentino' },
];
const movementColumns: Array<{ sort: FinanceMovementSort; label: string }> = [
  { sort: 'occurredOn', label: 'Data' },
  { sort: 'amount', label: 'Valor' },
  { sort: 'createdAt', label: 'Registro' },
];

export function CompanyFinance({
  capabilities,
  consolidated = false,
  timeZone = getBrowserTimeZone(),
  today,
  view,
  onView,
  onOpenObligations,
}: {
  capabilities: FinanceCapabilities;
  /// Sem unidade escolhida numa empresa de várias (ADR 0039): contas e extratos das unidades
  /// alcançadas só para consulta; conciliação e fechamento pedem a unidade.
  consolidated?: boolean;
  timeZone?: string;
  today: CivilDate;
  view: FinanceView;
  onView: ViewSetter<FinanceView>;
  onOpenObligations: (direction: FinancialObligationDirection) => void;
}) {
  /// Extrato e conciliação escolhem entre todas as contas ativas; os filtros da aba Contas
  /// recortam só a lista dela, numa consulta à parte.
  const accounts = useQuery({
    queryKey: ['company', 'finance', 'accounts'],
    queryFn: () => financeAccountsApi.list({ sort: 'displayName', direction: 'asc' }),
    retry: false,
  });
  const accountFilters = {
    search: view.accountSearch.trim() || undefined,
    activity: view.activity,
    kind: view.kind,
    currency: view.currency,
    sort: 'displayName' as const,
    direction: 'asc' as const,
  };
  const filteredAccounts = useQuery({
    queryKey: ['company', 'finance', 'accounts', accountFilters],
    queryFn: () => financeAccountsApi.list(accountFilters),
    enabled: view.tab === 'accounts',
    placeholderData: keepPreviousData,
    retry: false,
  });
  const activeAccounts = accounts.data?.filter((account) => account.active) ?? [];
  const selectedId = view.account ?? activeAccounts[0]?.id;

  useEffect(() => {
    if (!view.account && selectedId) onView({ account: selectedId }, { replace: true });
  }, [onView, selectedId, view.account]);

  return (
    <section aria-labelledby="finance-title">
      <PageHeader
        trail="Financeiro"
        id="finance-title"
        title="Financeiro"
        description="Contas, saldo derivado e extrato dos movimentos da empresa."
      />
      {consolidated ? (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-surface-border bg-surface-card p-4 text-sm text-content-secondary"
        >
          Visão consolidada: contas e extratos de todas as suas unidades, só para consulta. Para
          lançar, conciliar ou fechar o mês, escolha uma unidade no seletor acima.
        </p>
      ) : null}
      <SegmentedControl
        tabs
        label="Áreas do financeiro"
        className="mb-5"
        value={view.tab}
        onChange={(tab) => onView(tab === 'reconciliation' ? { tab, offset: 0 } : { tab })}
        options={[
          { value: 'statement', label: 'Extrato' },
          { value: 'reconciliation', label: 'Conciliação' },
          { value: 'rules', label: 'Regras' },
          { value: 'closing', label: 'Fechamento' },
          { value: 'accounts', label: 'Contas' },
        ]}
      />
      {consolidated && (view.tab === 'reconciliation' || view.tab === 'closing') ? (
        <EmptyCard
          icon={Landmark}
          title="Escolha uma unidade"
          description={
            view.tab === 'closing'
              ? 'Cada unidade fecha o próprio caixa. Escolha uma no seletor acima para ver ou fechar o mês.'
              : 'A conciliação acontece dentro de uma unidade. Escolha uma no seletor acima para ver o extrato e os compromissos dela.'
          }
        />
      ) : view.tab === 'reconciliation' ? (
        <ReconciliationPanel
          accounts={activeAccounts}
          capabilities={{
            canSettle: capabilities.canSettle,
            canManageRules: capabilities.canManageRules,
          }}
          today={today}
          view={view}
          onView={onView}
        />
      ) : view.tab === 'rules' ? (
        <ReconciliationRulesPanel
          accounts={activeAccounts}
          capabilities={{ canManage: capabilities.canManageRules }}
          today={today}
        />
      ) : view.tab === 'closing' ? (
        <CompanyClosing
          capabilities={{ canClose: capabilities.canClose, canReopen: capabilities.canReopen }}
          today={today}
          onOpenReconciliation={() =>
            onView({ tab: 'reconciliation', state: financeViewDefaults.state, offset: 0 })
          }
          onOpenObligations={onOpenObligations}
        />
      ) : view.tab === 'accounts' ? (
        <AccountsPanel
          accounts={filteredAccounts.data ?? []}
          showUnits={consolidated}
          loading={filteredAccounts.isLoading}
          error={filteredAccounts.error}
          capabilities={capabilities}
          view={view}
          onView={onView}
        />
      ) : (
        <StatementPanel
          timeZone={timeZone}
          canUndoImport={!consolidated && capabilities.canCancelMovement}
          accounts={activeAccounts}
          selectedId={selectedId}
          loadingAccounts={accounts.isLoading}
          accountsError={accounts.error}
          capabilities={capabilities}
          today={today}
          view={view}
          onView={onView}
        />
      )}
    </section>
  );
}

/// O que a importação fez, inclusive o que as regras de classificação fizeram sozinhas: a pessoa
/// precisa saber que algo foi automático para ir conferir.
function importedMessage(importedRows: number, classifiedRows: number, skippedRows = 0): string {
  const imported = `${importedRows} ${importedRows === 1 ? 'movimento importado' : 'movimentos importados'}.`;
  const result =
    skippedRows > 0
      ? `${imported} ${skippedRows} ${skippedRows === 1 ? 'movimento já importado ignorado' : 'movimentos já importados ignorados'}.`
      : imported;
  if (classifiedRows === 0) return result;
  return `${result} ${classifiedRows} ${
    classifiedRows === 1 ? 'classificado automaticamente' : 'classificados automaticamente'
  } pelas regras.`;
}

function StatementPanel({
  timeZone,
  canUndoImport,
  accounts,
  selectedId,
  loadingAccounts,
  accountsError,
  capabilities,
  today,
  view,
  onView,
}: {
  timeZone: string;
  canUndoImport: boolean;
  accounts: FinanceAccount[];
  selectedId: string | undefined;
  loadingAccounts: boolean;
  accountsError: unknown;
  capabilities: FinanceCapabilities;
  today: CivilDate;
  view: FinanceView;
  onView: ViewSetter<FinanceView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const period = useOperationalPeriod(today);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FinanceMovement>();
  const [cancelTarget, setCancelTarget] = useState<FinanceMovement>();
  const selected = accounts.find((account) => account.id === selectedId);
  /// O recorte de dias manda no mês de trabalho enquanto existir: quem aponta dois dias na grade
  /// quer esses dois dias, mesmo fora do mês da barra. Sem recorte, o mês continua valendo.
  const ranged = Boolean(view.from ?? view.to);
  const from = ranged ? view.from : period.from;
  const to = ranged ? view.to : period.to;
  const filters = selectedId
    ? {
        financeAccountId: selectedId,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        flow: view.flow,
        status: view.status,
        ...(view.search.trim() ? { search: view.search.trim() } : {}),
        sort: view.sort,
        direction: view.direction,
        limit: view.limit,
        offset: view.offset,
      }
    : null;
  const page = useQuery({
    queryKey: ['company', 'finance', 'movements', filters],
    queryFn: () => financeMovementsApi.list(filters!),
    enabled: Boolean(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const total = page.data?.total ?? 0;
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['company', 'finance', 'movements'] });
  };
  const chips = [
    ...choiceChips('flow', view.flow, flowOptions, (flow) => onView({ flow })),
    ...choiceChips('status', view.status, movementStatusOptions, (status) => onView({ status })),
    ...(ranged
      ? [
          {
            key: 'range',
            label: formatCivilRange(
              { from: view.from ?? null, to: view.to ?? null },
              'Mês de trabalho',
            ),
            onRemove: () => onView({ from: undefined, to: undefined, offset: 0 }),
          },
        ]
      : []),
  ];
  function toggleSort(sort: FinanceMovementSort) {
    onView(
      view.sort === sort
        ? { direction: view.direction === 'asc' ? 'desc' : 'asc' }
        : { sort, direction: sort === 'occurredOn' || sort === 'createdAt' ? 'desc' : 'asc' },
    );
  }

  if (loadingAccounts) return <LoadingCard label="Carregando contas financeiras" />;
  if (accountsError) return <ErrorCard message={getApiError(accountsError)} />;
  if (accounts.length === 0)
    return (
      <EmptyCard
        icon={Landmark}
        title="Nenhuma conta ativa"
        description="Cadastre ou reative uma conta na aba Contas antes de lançar movimentos."
      />
    );
  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:max-w-sm">
          <Select
            label="Conta do extrato"
            value={selectedId ?? ''}
            options={accounts.map((account) => ({ value: account.id, label: account.displayName }))}
            onValueChange={(account) => onView({ account, offset: 0 })}
          />
        </div>
        {selected ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setHistoryOpen(true)}>
              Importações do extrato
            </Button>
            {capabilities.canCreateMovement ? (
              <>
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  <FileUp className="size-4" />
                  Importar extrato
                </Button>
                {capabilities.canSettle ? (
                  <Button variant="outline" onClick={() => setTransferOpen(true)}>
                    Transferir entre contas
                  </Button>
                ) : null}
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" />
                  Novo movimento
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      {page.data ? (
        <SummaryStrip
          label="Resumo do extrato"
          items={[
            {
              label: 'Saldo inicial',
              value: formatCurrency(page.data.openingBalanceCents, page.data.currency),
            },
            {
              label: 'Entradas',
              value: formatCurrency(page.data.inflowCents, page.data.currency),
              tone: 'positive',
            },
            {
              label: 'Saídas',
              value: formatCurrency(page.data.outflowCents, page.data.currency),
              tone: 'warning',
            },
            {
              label: 'Saldo final',
              value: formatCurrency(page.data.closingBalanceCents, page.data.currency),
            },
          ]}
        />
      ) : null}
      <FilterBar
        searchId="movement-search"
        searchLabel="Buscar movimento"
        searchPlaceholder="Descrição"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ flow: [], status: [], from: undefined, to: undefined })}
        count={`${total} ${total === 1 ? 'movimento' : 'movimentos'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'movimento' : 'movimentos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <MultiFilter
          label="Fluxo"
          itemNoun="fluxos"
          allLabel="Entradas e saídas"
          options={flowOptions}
          value={view.flow}
          onChange={(flow) => onView({ flow })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias</span>
          <DateRangePicker
            label="Período do extrato"
            value={{ from: view.from ?? null, to: view.to ?? null }}
            onValueChange={(next) =>
              onView({ from: next.from ?? undefined, to: next.to ?? undefined, offset: 0 })
            }
            today={today}
            emptyLabel="Mês de trabalho"
          />
        </div>
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todos"
          options={movementStatusOptions}
          value={view.status}
          onChange={(status) => onView({ status })}
        />
      </FilterBar>
      {page.isLoading ? (
        <LoadingCard label="Carregando extrato" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={Landmark}
          title="Nenhum movimento encontrado"
          description="Ajuste os filtros ou lance o primeiro movimento desta conta."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label="Ordenação dos movimentos"
                columns={movementColumns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.movements.map((movement) => (
                  <li key={movement.id}>
                    <MovementCard
                      movement={movement}
                      currency={page.data.currency}
                      capabilities={capabilities}
                      onEdit={() => setEditTarget(movement)}
                      onCancel={() => setCancelTarget(movement)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Extrato da conta {selected?.displayName}</caption>
              <colgroup>
                <col className="w-[12%]" />
                <col className="w-[15%]" />
                <col className="w-[13%]" />
                <col className="w-[52%]" />
                <col className="w-[8%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-surface-border">
                  {movementColumns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="px-5 py-3 text-xs font-semibold text-content/70">Descrição</th>
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.movements.map((movement) => (
                  <tr
                    key={movement.id}
                    className={movement.status === 'CANCELED' ? 'opacity-60' : ''}
                  >
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatCivilDate(movement.occurredOn)}
                    </td>
                    <td
                      className={`px-5 py-4 text-sm font-bold tabular-nums ${movement.direction === 'INFLOW' ? 'text-positive' : 'text-warning'}`}
                    >
                      {movement.direction === 'INFLOW' ? '+' : '−'}{' '}
                      {formatCurrency(movement.amountCents, page.data.currency)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {movementKindLabel(movement)}
                    </td>
                    <td className="px-5 py-4">
                      <strong className="block break-words text-sm">{movement.description}</strong>
                      {movement.status === 'CANCELED' ? (
                        <StatusPill tone="neutral">Cancelado</StatusPill>
                      ) : null}
                    </td>
                    <td className="px-3 py-4">
                      <MovementActions
                        movement={movement}
                        capabilities={capabilities}
                        onEdit={() => setEditTarget(movement)}
                        onCancel={() => setCancelTarget(movement)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'movimento' : 'movimentos')}
          />
        </div>
      )}
      {selected ? (
        <MovementDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          account={selected}
          today={today}
          onSaved={async () => {
            await refresh();
            setCreateOpen(false);
            snackbar.success('Movimento lançado.');
          }}
        />
      ) : null}
      {transferOpen && selected ? (
        <TransferDialog
          sourceAccountId={selected.id}
          today={today}
          onClose={() => setTransferOpen(false)}
        />
      ) : null}
      {importOpen && selected ? (
        <StatementImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          account={selected}
          accounts={accounts}
          onSaved={async ({ importedRows, classifiedRows, skippedRows, financeAccountId }) => {
            onView({ account: financeAccountId, offset: 0 });
            await refresh();
            setImportOpen(false);
            snackbar.success(importedMessage(importedRows, classifiedRows, skippedRows));
          }}
        />
      ) : null}
      {historyOpen && selected ? (
        <StatementImportHistory
          account={selected}
          canUndo={canUndoImport}
          canRestore={canUndoImport && capabilities.canCreateMovement}
          timeZone={timeZone}
          onClose={() => setHistoryOpen(false)}
          onChanged={async (count, restored) => {
            setHistoryOpen(false);
            await queryClient.invalidateQueries({ queryKey: ['company'] });
            snackbar.success(
              `Importação ${restored ? 'restaurada' : 'desfeita'}. ${count} ${count === 1 ? 'movimento' : 'movimentos'} ${restored ? (count === 1 ? 'restaurado' : 'restaurados') : count === 1 ? 'retirado do extrato' : 'retirados do extrato'}.`,
            );
          }}
        />
      ) : null}
      {editTarget ? (
        <DescriptionDialog
          movement={editTarget}
          onOpenChange={() => setEditTarget(undefined)}
          onSaved={async () => {
            await refresh();
            setEditTarget(undefined);
            snackbar.success('Descrição atualizada.');
          }}
        />
      ) : null}
      {cancelTarget ? (
        <CancelDialog
          movement={cancelTarget}
          onOpenChange={() => setCancelTarget(undefined)}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company'] });
            setCancelTarget(undefined);
            snackbar.success(
              cancelTarget.transferGroupId ? 'Transferência desfeita.' : 'Movimento cancelado.',
            );
          }}
        />
      ) : null}
    </>
  );
}

function MovementCard({
  movement,
  currency,
  capabilities,
  onEdit,
  onCancel,
}: {
  movement: FinanceMovement;
  currency: Currency;
  capabilities: FinanceCapabilities;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const Icon = movement.direction === 'INFLOW' ? ArrowDownLeft : ArrowUpRight;
  return (
    <div className={`p-4 ${movement.status === 'CANCELED' ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <strong className="block truncate text-sm">{movement.description}</strong>
          <span className="text-xs text-content-muted">
            {formatCivilDate(movement.occurredOn)} · {movementKindLabel(movement)}
          </span>
          <div
            className={`mt-1 font-bold tabular-nums ${movement.direction === 'INFLOW' ? 'text-positive' : 'text-warning'}`}
          >
            {movement.direction === 'INFLOW' ? '+' : '−'}{' '}
            {formatCurrency(movement.amountCents, currency)}
          </div>
          {movement.status === 'CANCELED' ? (
            <StatusPill tone="neutral">Cancelado</StatusPill>
          ) : null}
        </div>
        <MovementActions
          movement={movement}
          capabilities={capabilities}
          onEdit={onEdit}
          onCancel={onCancel}
        />
      </div>
    </div>
  );
}

function MovementActions({
  movement,
  capabilities,
  onEdit,
  onCancel,
}: {
  movement: FinanceMovement;
  capabilities: FinanceCapabilities;
  onEdit: () => void;
  onCancel: () => void;
}) {
  if (movement.status === 'CANCELED') return null;
  return (
    <div className="flex justify-end gap-1">
      {capabilities.canUpdateMovement ? (
        <IconButton
          label="Editar descrição"
          icon={<PencilLine className="size-4" />}
          onClick={onEdit}
        />
      ) : null}
      {(movement.transferGroupId ? capabilities.canSettle : capabilities.canCancelMovement) ? (
        <IconButton
          label={movement.transferGroupId ? 'Desfazer transferência' : 'Cancelar movimento'}
          tone="danger"
          icon={<Ban className="size-4" />}
          onClick={onCancel}
        />
      ) : null}
    </div>
  );
}

function movementKindLabel(movement: FinanceMovement): string {
  if (movement.kind === 'OPENING_BALANCE') return 'Posição inicial';
  if (movement.transferGroupId) return 'Transferência entre contas';
  if (movement.importId) return 'Importado';
  return 'Manual';
}

function StatementImportDialog({
  accounts,
  open,
  onOpenChange,
  account,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: FinanceAccount;
  accounts: FinanceAccount[];
  onSaved: (result: StatementImportResult & { financeAccountId: string }) => Promise<void>;
}) {
  const importAccounts = useQuery({
    queryKey: ['company', 'finance', 'import-accounts'],
    enabled: open,
    queryFn: () =>
      financeAccountsApi.list({
        activity: ['ACTIVE'],
        sort: 'displayName',
        direction: 'asc',
      }),
    retry: false,
  });
  const [accountId, setAccountId] = useState(account.id);
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState<'csv' | 'xlsx'>();

  async function download(format: 'csv' | 'xlsx') {
    setDownloading(format);
    setError(undefined);
    try {
      const blob = await financeMovementsApi.downloadTemplate(format);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `modelo-movimentos.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setDownloading(undefined);
    }
  }

  async function save() {
    if (!file) {
      setError('Escolha o CSV do Banco Inter, o extrato da Ton ou o modelo preenchido.');
      return;
    }
    if (file.size > 5_000_000) {
      setError('O arquivo excede o limite de 5 MB.');
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const result = await financeMovementsApi.importStatement(accountId, file);
      await onSaved({ ...result, financeAccountId: accountId });
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Importar extrato"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Importar movimentos
          </Button>
        </div>
      }
      description="Escolha a conta financeira deste extrato. Movimentos já importados são ignorados; somente os novos são incluídos. O mesmo arquivo não pode ser importado novamente."
    >
      <TransferChoice
        label="Conta para importar"
        value={accountId}
        onChange={setAccountId}
        options={(importAccounts.data ?? accounts).map((a) => ({
          value: a.id,
          label: `${a.displayName} · ${a.businessUnitName}`,
        }))}
      />
      <div className="mt-4 rounded-xl border border-surface-border bg-surface-elevated p-4">
        <strong className="text-sm">Extratos do Inter e da Ton</strong>
        <p className="mt-1 text-sm text-content-muted">
          Envie o CSV do Inter ou o extrato da Ton. Para outros formatos, baixe o modelo, preencha
          Data, Descrição, Tipo e Valor e envie nesta tela.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={downloading === 'xlsx'}
            onClick={() => void download('xlsx')}
          >
            <Download className="size-4" /> Modelo XLSX
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={downloading === 'csv'}
            onClick={() => void download('csv')}
          >
            <Download className="size-4" /> Modelo CSV
          </Button>
        </div>
      </div>
      <label className="mt-4 block cursor-pointer rounded-xl border border-dashed border-surface-border p-5 text-center transition hover:border-brand-500">
        <FileUp className="mx-auto mb-2 size-6 text-brand-400" />
        <span className="block text-sm font-semibold">{file?.name ?? 'Escolher arquivo'}</span>
        <span className="mt-1 block text-xs text-content-muted">
          CSV do Inter, extrato da Ton (.xls ou .xlsx) ou modelo preenchido · até 5 MB
        </span>
        <input
          className="sr-only"
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(event) => setFile(event.target.files?.[0])}
        />
      </label>
      {error ? <RequestAlert message={error} /> : null}
    </Modal>
  );
}

function MovementDialog({
  open,
  onOpenChange,
  account,
  today,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: FinanceAccount;
  today: CivilDate;
  onSaved: () => Promise<void>;
}) {
  const [date, setDate] = useState<CivilDate | null>(today);
  const [kind, setKind] = useState<FinanceMovementKind>('MANUAL');
  const [direction, setDirection] = useState<FinanceMovementDirection>('OUTFLOW');
  const [amount, setAmount] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!date || !amount || description.trim().length < 2) {
      setError('Informe data, valor e descrição do movimento.');
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await financeMovementsApi.create({
        financeAccountId: account.id,
        occurredOn: date,
        kind,
        direction,
        amountCents: amount,
        description,
      });
      await onSaved();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Novo movimento"
      description={`Lançamento em ${account.displayName}. Conta, data, tipo, direção e valor não poderão ser editados.`}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Tipo"
          value={kind}
          options={[
            { value: 'MANUAL', label: 'Movimento manual' },
            { value: 'OPENING_BALANCE', label: 'Posição inicial' },
          ]}
          onValueChange={(value) => setKind(value as FinanceMovementKind)}
        />
        <Select
          label="Direção"
          value={direction}
          options={[
            { value: 'INFLOW', label: 'Entrada' },
            { value: 'OUTFLOW', label: 'Saída' },
          ]}
          onValueChange={(value) => setDirection(value as FinanceMovementDirection)}
        />
        <DatePicker label="Data" value={date} onValueChange={setDate} today={today} />
        <CurrencyInput
          label="Valor"
          currency={account.currency}
          value={amount}
          onValueChange={setAmount}
        />
        <div className="sm:col-span-2">
          <Input
            label="Descrição"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={240}
          />
        </div>
      </div>
      {error ? <RequestAlert message={error} /> : null}
      <DialogActions
        loading={saving}
        onCancel={() => onOpenChange(false)}
        onConfirm={() => void save()}
        confirm="Lançar movimento"
      />
    </Modal>
  );
}

function DescriptionDialog({
  movement,
  onOpenChange,
  onSaved,
}: {
  movement: FinanceMovement;
  onOpenChange: () => void;
  onSaved: () => Promise<void>;
}) {
  const [description, setDescription] = useState(movement.description);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      await financeMovementsApi.update(movement.id, description);
      await onSaved();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Editar descrição"
      description="Somente o rótulo pode ser alterado; o fato financeiro permanece intacto."
    >
      <Input
        label="Descrição"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        maxLength={240}
      />
      {error ? <RequestAlert message={error} /> : null}
      <DialogActions
        loading={saving}
        onCancel={onOpenChange}
        onConfirm={() => void save()}
        confirm="Salvar descrição"
      />
    </Modal>
  );
}
function CancelDialog({
  movement,
  onOpenChange,
  onSaved,
}: {
  movement: FinanceMovement;
  onOpenChange: () => void;
  onSaved: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      if (movement.transferGroupId)
        await api.post(`/tenant/financial-transfers/${movement.transferGroupId}/undo`, { reason });
      else await financeMovementsApi.cancel(movement.id, reason);
      await onSaved();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onOpenChange}>
            Voltar
          </Button>
          <Button
            loading={saving}
            className="bg-danger text-white shadow-none hover:bg-danger/85"
            onClick={() => void save()}
          >
            {movement.transferGroupId ? 'Desfazer transferência' : 'Cancelar movimento'}
          </Button>
        </div>
      }
      onOpenChange={onOpenChange}
      title={movement.transferGroupId ? 'Desfazer transferência' : 'Cancelar movimento'}
      description={
        movement.transferGroupId
          ? 'O par criado junto será cancelado nas duas contas. Se veio dos extratos, apenas o vínculo será desfeito.'
          : 'O movimento permanece no extrato e deixa de compor o saldo.'
      }
    >
      <Input
        label={movement.transferGroupId ? 'Motivo para desfazer' : 'Motivo do cancelamento'}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        maxLength={240}
      />
      {error ? <RequestAlert message={error} /> : null}
    </Modal>
  );
}

function AccountsPanel({
  accounts,
  showUnits,
  loading,
  error,
  capabilities,
  view,
  onView,
}: {
  accounts: FinanceAccount[];
  showUnits: boolean;
  loading: boolean;
  error: unknown;
  capabilities: FinanceCapabilities;
  view: FinanceView;
  onView: ViewSetter<FinanceView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [dialog, setDialog] = useState<FinanceAccount | null | undefined>();
  const [activation, setActivation] = useState<FinanceAccount>();
  const [requestError, setRequestError] = useState<string>();
  const refresh = async () =>
    queryClient.invalidateQueries({ queryKey: ['company', 'finance', 'accounts'] });
  const chips = [
    ...choiceChips('activity', view.activity, activityOptions, (activity) => onView({ activity })),
    ...choiceChips('kind', view.kind, accountKinds, (kind) => onView({ kind })),
    ...choiceChips('currency', view.currency, currencies, (currency) => onView({ currency })),
  ];
  async function toggle(account: FinanceAccount) {
    setRequestError(undefined);
    try {
      await financeAccountsApi.setActive(account.id, !account.active);
      await refresh();
      setActivation(undefined);
      snackbar.success(account.active ? 'Conta arquivada.' : 'Conta reativada.');
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }
  return (
    <>
      <div className="mb-4 flex justify-end">
        {capabilities.canCreateAccount ? (
          <Button onClick={() => setDialog(null)}>
            <Plus className="size-4" />
            Cadastrar conta
          </Button>
        ) : null}
      </div>
      <FilterBar
        searchId="account-search"
        searchLabel="Buscar conta"
        searchPlaceholder="Nome ou instituição"
        search={view.accountSearch}
        onSearchChange={(accountSearch) => onView({ accountSearch }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: [], kind: [], currency: [] })}
        count={`${accounts.length} ${accounts.length === 1 ? 'conta' : 'contas'}`}
        resultsLabel={`Ver ${accounts.length} ${accounts.length === 1 ? 'conta' : 'contas'}`}
      >
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todas"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <MultiFilter
          label="Tipo"
          itemNoun="tipos"
          allLabel="Todos os tipos"
          options={accountKinds}
          value={view.kind}
          onChange={(kind) => onView({ kind })}
        />
        <MultiFilter
          label="Moeda"
          itemNoun="moedas"
          allLabel="Todas as moedas"
          options={currencies}
          value={view.currency}
          onChange={(currency) => onView({ currency })}
        />
      </FilterBar>
      {loading ? (
        <LoadingCard label="Carregando contas financeiras" />
      ) : error ? (
        <ErrorCard message={getApiError(error)} />
      ) : accounts.length === 0 ? (
        <EmptyCard
          icon={Landmark}
          title="Nenhuma conta encontrada"
          description="Ajuste os filtros ou cadastre a primeira conta da empresa."
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {accounts.map((account) => (
            <li
              key={account.id}
              className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-soft"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <strong className="block truncate">{account.displayName}</strong>
                  <span className="text-xs text-content-muted">
                    {kindLabels[account.kind]} · {account.currency}
                    {account.institutionName ? ` · ${account.institutionName}` : ''}
                  </span>
                  {showUnits ? (
                    <span className="mt-1 block truncate text-xs text-content-faint">
                      {account.businessUnitName}
                    </span>
                  ) : null}
                  {account.active ? null : (
                    <div className="mt-2">
                      <StatusPill tone="neutral">Arquivada</StatusPill>
                    </div>
                  )}
                </div>
                <div className="flex">
                  {capabilities.canUpdateAccount ? (
                    <IconButton
                      label="Editar conta"
                      icon={<PencilLine className="size-4" />}
                      onClick={() => setDialog(account)}
                    />
                  ) : null}
                  {account.active ? (
                    capabilities.canArchiveAccount ? (
                      <IconButton
                        label="Arquivar conta"
                        tone="danger"
                        icon={<Archive className="size-4" />}
                        onClick={() => setActivation(account)}
                      />
                    ) : null
                  ) : capabilities.canUpdateAccount ? (
                    <IconButton
                      label="Reativar conta"
                      icon={<RotateCcw className="size-4" />}
                      onClick={() => setActivation(account)}
                    />
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {dialog !== undefined ? (
        <AccountDialog
          account={dialog}
          onOpenChange={() => setDialog(undefined)}
          onSaved={async () => {
            await refresh();
            setDialog(undefined);
            snackbar.success(dialog ? 'Conta atualizada.' : 'Conta cadastrada.');
          }}
        />
      ) : null}
      {activation ? (
        <Modal
          open
          onOpenChange={() => setActivation(undefined)}
          title={activation.active ? 'Arquivar conta' : 'Reativar conta'}
          description={
            activation.active
              ? 'A conta deixa de receber movimentos novos; o saldo e o extrato permanecem.'
              : 'A conta volta a aceitar movimentos.'
          }
        >
          <p className="text-sm">
            {activation.active ? 'Arquivar' : 'Reativar'} <strong>{activation.displayName}</strong>?
          </p>
          {requestError ? <RequestAlert message={requestError} /> : null}
          <DialogActions
            onCancel={() => setActivation(undefined)}
            onConfirm={() => void toggle(activation)}
            confirm={activation.active ? 'Arquivar conta' : 'Reativar conta'}
            danger={activation.active}
          />
        </Modal>
      ) : null}
    </>
  );
}

function AccountDialog({
  account,
  onOpenChange,
  onSaved,
}: {
  account: FinanceAccount | null;
  onOpenChange: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(account?.displayName ?? '');
  const [kind, setKind] = useState<FinanceAccountKind>(account?.kind ?? 'BANK');
  const [currency, setCurrency] = useState<Currency>(account?.currency ?? 'BRL');
  const [institution, setInstitution] = useState(account?.institutionName ?? '');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      if (account)
        await financeAccountsApi.update(account.id, {
          displayName: name,
          institutionName: institution.trim() || null,
        });
      else
        await financeAccountsApi.create({
          displayName: name,
          kind,
          currency,
          institutionName: institution.trim() || null,
        });
      await onSaved();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title={account ? 'Editar conta' : 'Cadastrar conta'}
      description={
        account
          ? 'Tipo e moeda são imutáveis para preservar o histórico.'
          : 'A conta será a origem do saldo e do extrato.'
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Input
            label="Nome da conta"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={160}
          />
        </div>
        <Select
          label="Tipo"
          value={kind}
          options={accountKinds}
          onValueChange={(value) => setKind(value as FinanceAccountKind)}
          disabled={Boolean(account)}
        />
        <Select
          label="Moeda"
          value={currency}
          options={currencies}
          onValueChange={(value) => setCurrency(value as Currency)}
          disabled={Boolean(account)}
        />
        <div className="sm:col-span-2">
          <Input
            label="Instituição"
            value={institution}
            onChange={(event) => setInstitution(event.target.value)}
            maxLength={160}
          />
        </div>
      </div>
      {error ? <RequestAlert message={error} /> : null}
      <DialogActions
        loading={saving}
        onCancel={onOpenChange}
        onConfirm={() => void save()}
        confirm={account ? 'Salvar conta' : 'Cadastrar conta'}
      />
    </Modal>
  );
}

function DialogActions({
  loading = false,
  onCancel,
  onConfirm,
  confirm,
  danger = false,
}: {
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  confirm: string;
  danger?: boolean;
}) {
  return (
    <div className="mt-6 flex justify-end gap-3 border-t border-surface-border pt-5">
      <Button type="button" variant="outline" onClick={onCancel}>
        Voltar
      </Button>
      <Button
        type="button"
        loading={loading}
        className={danger ? 'bg-danger text-white shadow-none hover:bg-danger/85' : undefined}
        onClick={onConfirm}
      >
        {confirm}
      </Button>
    </div>
  );
}
